<!-- un usuario que no es de tipo API no tiene peticiones registradas: se dice, en lugar de dejar el panel
     vacío como si fuese un error -->
<p-messages severity="info" *ngIf="userLoaded && !isApiUser">
    <ng-template pTemplate>
        <div class="ml-2">
            Solo se registran las peticiones de los usuarios de tipo <strong>API</strong>. Cambia el tipo de
            usuario en su ficha para empezar a registrar su uso.
        </div>
    </ng-template>
</p-messages>

<div *ngIf="userLoaded && isApiUser">

    <div class="flex gap-2 flex-wrap align-items-end">
        <div class="input-field" style="min-width:170px">
            <label for="rangeDays">Periodo</label>
            <p-dropdown [options]="rangeOptions" optionLabel="name" optionValue="value" [(ngModel)]="rangeDays"
                (onChange)="onRangeChange()" styleClass="w-full" appendTo="body"></p-dropdown>
        </div>
        <div class="input-field" style="max-width:134px">
            <label for="dateFrom">Desde</label>
            <p-calendar [firstDayOfWeek]="1" [(ngModel)]="dateFrom" [showIcon]="true" [showTime]="false"
                dateFormat="dd/mm/yy" appendTo="body"></p-calendar>
        </div>
        <div class="input-field" style="max-width:134px">
            <label for="dateTo">Hasta</label>
            <p-calendar [firstDayOfWeek]="1" [(ngModel)]="dateTo" [showIcon]="true" [showTime]="false"
                dateFormat="dd/mm/yy" appendTo="body"></p-calendar>
        </div>
        <!-- el código de respuesta es un filtro de toda la pantalla, igual que el periodo: se aplica a los
             indicadores, a las gráficas, a los endpoints y al registro -->
        <div class="input-field" style="min-width:215px">
            <label for="statusFilter">Código de respuesta</label>
            <p-dropdown [options]="statusFilterOptions" optionLabel="name" optionValue="value" [group]="true"
                optionGroupLabel="name" optionGroupChildren="items" [(ngModel)]="statusFilter"
                (onChange)="onStatusFilterChange()" showClear placeholder="Cualquiera"
                [disabled]="isAggregatedSource" styleClass="w-full" appendTo="body"></p-dropdown>
        </div>
        <div class="input-field flex">
            <button pButton ngClass="mt-3" label="Buscar" icon="pi pi-search" (click)="search()"></button>
        </div>
    </div>

    <!-- los días se agregan por día UTC y no se pueden reagrupar después: hay que decirlo -->
    <div class="mt-2 text-xs text-color-secondary">
        Los días y las horas se cuentan en horario UTC.
        <span *ngIf="isAggregatedSource">
            · Parte del periodo sale del resumen diario porque su detalle ya se ha purgado: el percentil 95 es
            estimado y no hay registro de peticiones individuales.
        </span>
        <span *ngIf="granularityNote as note">· {{note}}</span>
        <span *ngIf="statusFilterLabel != ''">
            · Filtrando por <strong>{{statusFilterLabel}}</strong>: los indicadores, las gráficas, los
            endpoints y el registro cuentan solo esas peticiones. El reparto por código se muestra siempre
            completo, porque es el selector del filtro.
        </span>
    </div>

    <!-- indicadores del periodo. Con el filtro puesto sobre un periodo purgado no hay nada que enseñar: los
         contadores saldrían a cero y se leerían como "no hubo tráfico" -->
    <div class="grid mt-3" *ngIf="summary != null && !isStatusFilterUnavailable">
        <div class="col-12 md:col-4 lg:col-2">
            <div class="surface-card border-round p-3 h-full">
                <div class="text-2xl font-bold">{{summary.totalRequests | number}}</div>
                <div class="text-xs text-color-secondary">peticiones</div>
            </div>
        </div>
        <div class="col-12 md:col-4 lg:col-2">
            <div class="surface-card border-round p-3 h-full">
                <div class="text-2xl font-bold">{{errorRatePercent}}%</div>
                <div class="text-xs text-color-secondary">errores ({{summary.errorRequests | number}})</div>
            </div>
        </div>
        <div class="col-12 md:col-4 lg:col-2">
            <div class="surface-card border-round p-3 h-full">
                <div class="text-2xl font-bold">{{summary.averageDurationMs | number:'1.0-1'}} ms</div>
                <div class="text-xs text-color-secondary">duración media</div>
            </div>
        </div>
        <div class="col-12 md:col-4 lg:col-2">
            <div class="surface-card border-round p-3 h-full">
                <div class="text-2xl font-bold">{{summary.p95DurationMs | number}} ms</div>
                <div class="text-xs text-color-secondary">
                    percentil 95<span *ngIf="isAggregatedSource"> (estimado)</span>
                </div>
            </div>
        </div>
        <div class="col-12 md:col-4 lg:col-2">
            <div class="surface-card border-round p-3 h-full">
                <div class="text-2xl font-bold">{{summary.distinctEndpoints | number}}</div>
                <div class="text-xs text-color-secondary">endpoints distintos</div>
            </div>
        </div>
        <div class="col-12 md:col-4 lg:col-2">
            <div class="surface-card border-round p-3 h-full">
                <div class="text-2xl font-bold">{{summary.distinctDays | number}}</div>
                <div class="text-xs text-color-secondary">
                    días con uso<span *ngIf="summary.lastRequestAt"> · última {{summary.lastRequestAt | date:'dd/MM HH:mm'}}</span>
                </div>
            </div>
        </div>
    </div>

    <div class="grid mt-2" *ngIf="summary != null && summary.totalRequests > 0">

        <div class="col-12 lg:col-7">
            <div class="surface-card border-round p-3 h-full">
                <div class="flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                    <span class="font-semibold">{{evolutionTitle}}</span>
                    <div class="flex gap-2">
                        <!-- input-field es lo que iguala el tamaño con los desplegables de los filtros -->
                        <div class="input-field flex-none" style="width:180px">
                            <p-dropdown [options]="granularityOptions" optionLabel="name" optionValue="value"
                                optionDisabled="disabled" [(ngModel)]="granularity"
                                (onChange)="onGranularityChange()" appendTo="body"></p-dropdown>
                        </div>
                        <div class="input-field flex-none" style="width:180px">
                            <p-dropdown [options]="seriesModeOptions" optionLabel="name" optionValue="value"
                                [(ngModel)]="seriesMode" (onChange)="onSeriesModeChange()" appendTo="body"></p-dropdown>
                        </div>
                    </div>
                </div>
                <!-- las vistas horarias se piden al seleccionarlas, así que hay un momento sin datos que
                     hay que ocupar: si no, la gráfica anterior se queda en pantalla como si fuese la nueva -->
                <div *ngIf="granularityLoading" class="flex align-items-center justify-content-center"
                    style="height:220px" role="status" aria-label="Cargando">
                    <i class="pi pi-spin pi-spinner text-2xl text-color-secondary"></i>
                </div>
                <!-- fallo de red o periodo que el servidor no puede servir: mejor decirlo que dejar una
                     gráfica en blanco, que se lee como un error del panel -->
                <div *ngIf="!granularityLoading && evolutionError != ''"
                    class="flex align-items-center justify-content-center text-center text-sm text-color-secondary px-3"
                    style="height:220px">
                    {{evolutionError}}
                </div>
                <p-chart *ngIf="!granularityLoading && evolutionError == ''" [type]="evolutionChartType"
                    [data]="evolutionChartData" [options]="evolutionChartOptions" height="220px"></p-chart>
            </div>
        </div>

        <div class="col-12 lg:col-5">
            <div class="surface-card border-round p-3 h-full">
                <div class="flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                    <span class="font-semibold">Códigos de estado</span>
                    <!-- el filtro se puede poner desde la propia tarta, así que el acuse de que está
                         puesto va aquí y no solo arriba, en el desplegable -->
                    <p-tag *ngIf="statusFilterLabel != ''" severity="info" styleClass="cursor-pointer"
                        (click)="clearStatusFilter()" pTooltip="Quitar el filtro">
                        <span>{{statusFilterLabel}}</span><i class="pi pi-times ml-2 text-xs"></i>
                    </p-tag>
                    <span *ngIf="statusFilterLabel == '' && hasDetailSource" class="text-xs text-color-secondary">
                        Pulsa una porción para filtrar la pantalla
                    </span>
                </div>
                <p-chart type="doughnut" [data]="statusChartData" [options]="statusChartOptions"
                    height="220px" (onDataSelect)="onStatusSliceSelect($event)"></p-chart>
            </div>
        </div>

        <div class="col-12">
            <div class="surface-card border-round p-3">
                <div class="font-semibold mb-2">Endpoints más llamados</div>
                <p-table [value]="topEndpoints" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr>
                            <th width="90">Método</th>
                            <th>Endpoint</th>
                            <th width="110" class="text-right">Peticiones</th>
                            <th width="100" class="text-right">Errores</th>
                            <th width="120" class="text-right">Media</th>
                            <th width="120" class="text-right">Máximo</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-endpoint>
                        <tr>
                            <td>{{endpoint.method}}</td>
                            <td class="text-color-secondary break-anywhere">{{endpoint.routeTemplate || '(sin ruta)'}}</td>
                            <td class="text-right">{{endpoint.requests | number}}</td>
                            <td class="text-right">{{endpoint.errors | number}}</td>
                            <td class="text-right">{{endpoint.averageDurationMs | number:'1.0-1'}} ms</td>
                            <td class="text-right">{{endpoint.maxDurationMs | number}} ms</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="6">Sin endpoints en el periodo.</td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
        </div>

    </div>

    <!-- el detalle de esas fechas ya se purgó y el agregado no guarda el código de cada petición: sin este
         aviso, los indicadores a cero se leerían como "no hubo tráfico" -->
    <p-messages severity="warn" *ngIf="!statsLoading && isStatusFilterUnavailable">
        <ng-template pTemplate>
            <div class="ml-2">
                No se puede filtrar por código en este periodo: su detalle ya se ha purgado y el resumen
                diario solo guarda los totales por clase.
                <a class="cursor-pointer underline" (click)="clearStatusFilter()">Quitar el filtro</a> o
                acortar el periodo.
            </div>
        </ng-template>
    </p-messages>

    <div class="mt-3 text-color-secondary"
        *ngIf="!statsLoading && !isStatusFilterUnavailable && summary != null && summary.totalRequests == 0">
        No hay peticiones en el periodo seleccionado<span *ngIf="statusFilterLabel != ''"> con el filtro
        <strong>{{statusFilterLabel}}</strong></span>.
    </div>

    <!-- registro de peticiones -->
    <h3 class="mt-4 mb-2">Registro de peticiones</h3>

    <div class="flex gap-2 flex-wrap align-items-end">
        <div class="input-field" style="min-width:150px">
            <label for="method">Método</label>
            <p-dropdown [options]="methodOptions" optionLabel="name" optionValue="value"
                [(ngModel)]="logFilters.method" (onChange)="reloadLog()" showClear styleClass="w-full"
                appendTo="body"></p-dropdown>
        </div>
        <div class="input-field p-input-icon-right" style="min-width:220px">
            <label for="routeTemplate">Endpoint</label>
            <input pInputText placeholder="Endpoint" [(ngModel)]="logFilters.routeTemplate"
                (keydown.enter)="reloadLog()" class="w-full" />
            <i *ngIf="logFilters.routeTemplate" class="pi pi-times cursor-pointer"
                (click)="logFilters.routeTemplate = null; reloadLog()"></i>
        </div>
        <div class="input-field" style="min-width:150px">
            <label for="minDurationMs">Duración mínima (ms)</label>
            <p-inputNumber [(ngModel)]="logFilters.minDurationMs" (onBlur)="reloadLog()" [min]="0"
                styleClass="w-full"></p-inputNumber>
        </div>
        <div class="input-field flex">
            <button pButton ngClass="mt-3" label="Limpiar" icon="pi pi-eraser" (click)="clearLogFilters()"
                severity="secondary"></button>
        </div>
    </div>

    <p-table styleClass="mt-3" [value]="logItems" [lazy]="true" (onLazyLoad)="loadLog($event)"
        [loading]="logLoading" [paginator]="true" [rows]="50" [totalRecords]="logTotal"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="Mostrando de {first} a {last} de un total de {totalRecords} peticiones"
        [rowsPerPageOptions]="[50,100,200]" [scrollable]="true" paginatorDropdownAppendTo="body">
        <ng-template pTemplate="header">
            <tr>
                <th pSortableColumn="date" width="160">Fecha <p-sortIcon field="date"></p-sortIcon></th>
                <th width="90">Método</th>
                <th width="220">Endpoint</th>
                <th>Ruta</th>
                <th width="110">Resultado</th>
                <th pSortableColumn="durationMs" width="110" class="text-right">Duración <p-sortIcon
                        field="durationMs"></p-sortIcon></th>
                <th width="130">IP</th>
                <th width="90" class="text-center">Detalles</th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
            <tr>
                <td>{{item.date | date:'dd/MM/yyyy HH:mm:ss'}}</td>
                <td>{{item.method}}</td>
                <td class="text-color-secondary break-anywhere">{{item.routeTemplate || '(sin ruta)'}}</td>
                <td class="break-anywhere">
                    <span>{{item.path}}</span>
                    <div class="text-xs text-color-secondary" *ngIf="item.queryString">{{item.queryString}}</div>
                </td>
                <td>
                    <p-tag [value]="item.statusCode == 0 ? 'cancelada' : item.statusCode"
                        [severity]="statusSeverity(item.statusCode)"></p-tag>
                </td>
                <td class="text-right">{{item.durationMs | number}} ms</td>
                <td class="text-xs" [pTooltip]="item.userAgent" tooltipPosition="left">{{item.ipAddress}}</td>
                <!-- solo las peticiones que fallaron tienen respuesta guardada; en el resto no hay nada que
                     abrir y el botón no sale -->
                <td class="text-center">
                    <p-button *ngIf="item.hasResponseBody" (click)="showResponse(item)" [rounded]="true"
                        [text]="true" severity="secondary" pTooltip="Ver la respuesta que devolvió la API"
                        tooltipPosition="left">
                        <i class="pi pi-file"></i>
                    </p-button>
                </td>
            </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
            <tr>
                <td colspan="8">No hay peticiones que coincidan con los filtros.</td>
            </tr>
        </ng-template>
    </p-table>

    <!-- respuesta que se le devolvió al integrador. Es lo que hace falta para explicarle un 500 o un 422 sin
         tener que reproducir la llamada -->
    <p-dialog header="Respuesta de la petición" [modal]="true" [(visible)]="responseVisible"
        [style]="{ width: '860px', maxWidth: '95vw' }" [dismissableMask]="true">
        <div *ngIf="responseItem != null" class="mb-3 text-sm">
            <div class="flex align-items-center gap-2 flex-wrap">
                <p-tag [value]="responseItem.statusCode == 0 ? 'cancelada' : ('' + responseItem.statusCode)"
                    [severity]="statusSeverity(responseItem.statusCode)"></p-tag>
                <span class="font-semibold">{{responseItem.method}}</span>
                <span class="break-anywhere">{{responseItem.path}}</span>
                <span class="text-color-secondary">{{responseItem.date | date:'dd/MM/yyyy HH:mm:ss'}}</span>
            </div>
            <div class="text-xs text-color-secondary break-anywhere mt-1" *ngIf="responseItem.queryString">
                {{responseItem.queryString}}
            </div>
        </div>

        <div *ngIf="responseLoading" class="flex align-items-center justify-content-center" style="height:120px"
            role="status" aria-label="Cargando">
            <i class="pi pi-spin pi-spinner text-2xl text-color-secondary"></i>
        </div>

        <div *ngIf="!responseLoading && responseNote != ''" class="text-sm text-color-secondary mb-2">
            {{responseNote}}
        </div>

        <pre *ngIf="!responseLoading && responseBody != ''" class="response-body">{{responseBody}}</pre>

        <div class="mt-3 flex justify-content-end">
            <p-button label="Cerrar" icon="pi pi-times" severity="secondary"
                (click)="responseVisible = false"></p-button>
        </div>
    </p-dialog>

</div>
